import type { DisplayCue } from "@expert/contentful";
import type { Logger } from "@expert/logging";
import type { KyInstance } from "ky";

import { HTTPError } from "ky";
import pDebounce from "p-debounce";

import type { SalesAnalytics } from "../sales-context/SalesContext.types";

import { useDetectedCuesStore } from "../stores/useDetectedCuesStore";
import { useProductOfferStore } from "../stores/useProductOfferStore";
import { EligibilityStatus, type EligibilityMeta, type EligibilityRequest, type EligibleProduct } from "../types";
import { applyCohortContentOverrides } from "./cohortContentOverrides";
import { type EligibilityErrorResponse, INVALID_MDN_CODE, InvalidMdnError } from "./eligibility.errors";
import { buildEligibilityResultLog } from "./eligibility.helpers";

export type { EligibilityRequest, EligibleProduct, EligibilityMeta };

const ELIGIBILITY_DEBOUNCE_TIME = 500;
let lastEligibilityCheckTime = 0;

export type CheckEligibilityResult = {
    productConfigs: EligibleProduct[];
    meta: EligibilityMeta;
    displayCues?: DisplayCue[];
};

// Store the logger and analytics for use in the debounced function
let currentLogger: Logger | null = null;
let currentAnalytics: SalesAnalytics | null = null;
let currentClient: KyInstance | null = null;

export const getEligibility = async (
    client: KyInstance,
    logger: Logger,
    analytics: SalesAnalytics,
    eligibility: EligibilityRequest,
    isCohortFlagOn = false,
): Promise<CheckEligibilityResult> => {
    currentLogger = logger;
    currentAnalytics = analytics;
    currentClient = client;

    const now = performance.now();
    const timeSinceLastCheck = now - lastEligibilityCheckTime;
    logger.info(
        { timeSinceLastCheck, isPrecheck: eligibility.eligibilityAttributes?.isPrecheck },
        "Eligibility check triggered",
    );

    if (lastEligibilityCheckTime > 0 && timeSinceLastCheck < ELIGIBILITY_DEBOUNCE_TIME) {
        logger.warn(
            {
                timeSinceLastCheck,
                isPrecheck: eligibility.eligibilityAttributes?.isPrecheck,
            },
            `Eligibility check triggered more than once within ${ELIGIBILITY_DEBOUNCE_TIME}ms. Debouncing...`,
        );
    }
    lastEligibilityCheckTime = now;

    return debouncedPerformEligibilityCheck(eligibility, isCohortFlagOn);
};

const debouncedPerformEligibilityCheck = pDebounce(
    async (eligibility: EligibilityRequest, isCohortFlagOn: boolean): Promise<CheckEligibilityResult> => {
        const logger = currentLogger!.child({ module: "eligibility" });
        const analytics = currentAnalytics!;

        try {
            const salesApiRes = await currentClient!
                .post("v2/eligibility", {
                    json: eligibility,
                    retry: 0,
                })
                .json<CheckEligibilityResult>();

            return handleResponse(logger, analytics, eligibility, salesApiRes, isCohortFlagOn);
        } catch (error: unknown) {
            // The eligibility API returns a 400 with `{ code: "INVALID_MDN", message }`
            // when the customer's phone number is invalid. Classify it into a typed error
            // here — this catch is the only place that holds the ky Response, whose body
            // can be read exactly once. The UI uses it to offer inline MDN correction.
            if (error instanceof HTTPError && error.response.status === 400) {
                let body: EligibilityErrorResponse | undefined;
                try {
                    body = await error.response.json<EligibilityErrorResponse>();
                } catch {
                    logger.warn({ error }, "eligibility 400 body was not valid JSON");
                }
                if (body?.code === INVALID_MDN_CODE) {
                    logger.warn({ code: body.code }, "eligibility rejected: invalid MDN");
                    throw new InvalidMdnError(body.message);
                }
            }
            logger.warn({ error }, "eligibility API call failed");
            throw error;
        }
    },
    ELIGIBILITY_DEBOUNCE_TIME,
);

const handleResponse = (
    logger: Logger,
    analytics: SalesAnalytics,
    eligibility: EligibilityRequest,
    checkEligibilityResult: CheckEligibilityResult,
    isCohortFlagOn: boolean,
): CheckEligibilityResult => {
    const isPrecheck = !!eligibility.eligibilityAttributes?.isPrecheck;

    // RDESQ-1988: cohort-only checklist verbiage swap. No-op when flag is off.
    checkEligibilityResult.productConfigs = applyCohortContentOverrides(
        checkEligibilityResult.productConfigs,
        isCohortFlagOn,
    );

    logger.info(buildEligibilityResultLog(eligibility, checkEligibilityResult.productConfigs), "Eligibility result");

    checkEligibilityResult.productConfigs.forEach((result) => {
        let dispatcher = analytics.dispatcher.withExtra({
            productSku: result.productSku,
            productOfferId: result.id,
            eligibilityStatus: result.status,
        });

        if (eligibility.reservationSid) {
            dispatcher = dispatcher.withIdentity("ReservationSid", eligibility.reservationSid);
        }

        void dispatcher.dispatchBusinessEvent("CustomerValidated");
        void dispatcher.dispatchAction("ApiResult", "EligibilityStatus");
        analytics.trackFullStoryEvent("EligibilityStatus", {
            productSku: result.productSku,
            eligibilityStatus: result.status,
            productOfferId: result.id,
            reservationSid: eligibility.reservationSid ?? "unknown",
            sessionId: eligibility.sessionId,
            partner: eligibility.partner,
            hasAddress: !!eligibility.address,
        });
    });

    // Skip store mutations on precheck. The precheck runs against the incoming
    // caller MDN before the expert has confirmed; populating the offer store
    // here causes CustomerOffers to skip its post-verification refetch (gating
    // logic treats the precheck-seeded products as already-fetched), and any
    // cues seeded from the precheck would reflect the incoming MDN, not what
    // the expert actually entered.
    if (!isPrecheck) {
        useProductOfferStore
            .getState()
            .updateProducts(
                eligibility.sessionId,
                checkEligibilityResult.productConfigs,
                checkEligibilityResult.displayCues,
            );

        const hasExistingCues = !!useDetectedCuesStore.getState().getDisplayCues(eligibility.sessionId).length;
        if (!hasExistingCues && checkEligibilityResult.displayCues) {
            useDetectedCuesStore
                .getState()
                .setDefaultPartnerCues(eligibility.sessionId, checkEligibilityResult.displayCues);
        }
    }

    analytics.sendToHoneycomb(eligibility.sessionId, {
        eligibility: checkEligibilityResult.productConfigs
            .filter((p) => p.status === EligibilityStatus.Eligible)
            .map((p) => ({
                productSku: p.productSku,
                productOfferId: p.id,
                eligibilityStatus: p.status,
                displayName: useProductOfferStore.getState().getProductInfo(eligibility.sessionId, p.productSku)
                    ?.displayName,
            })),
    });

    return checkEligibilityResult;
};
